import React, { useMemo } from "react";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { TextField, Typography, Box } from "@mui/material";
import dayjs from "dayjs";
import { formatThousands, stripCommas } from "./ForecastDrvUtils";

const columnHeaders = [
  { key: "forecastedTemp", label: "Forecasted Temperature °F", allowDecimal: true },
  { key: "totalDRV", label: "Total DRV Sendout**", allowDecimal: false },
  { key: "cspFirm", label: "CSP Firm", allowDecimal: false },
  { key: "cspInterruptible", label: "CSP Interruptible", allowDecimal: false },
  { key: "wglSendout", label: "WGL Sendout", allowDecimal: false },
];

// ── Darker text for disabled/read-only fields ──────────────────────────────
// MUI's default disabled color is a light gray. WebkitTextFillColor is
// included because Chrome/Safari use it (not `color`) to paint disabled
// and readOnly text inputs, so both need to be forced with !important.
const disabledFieldSx = {
  "& .Mui-disabled": {
    color: "#1a3a5c !important",
    WebkitTextFillColor: "#1a3a5c !important",
  },
  "& .MuiInputBase-input.Mui-disabled": {
    color: "#1a3a5c !important",
    WebkitTextFillColor: "#1a3a5c !important",
  },
};

const ForecastDrvTable = ({ startDate, dayCount = 0, rowIndexOffset = 0, rows, onRowChange, isAdmin }) => {
  const gasdays = Array.from({ length: dayCount }, (_, i) =>
    startDate ? dayjs(startDate).add(i, "day") : null
  );

  const handleChange = (rowIndex, field, value, allowDecimal) => {
    if (allowDecimal) {
      // Forecasted Temperature: free text, no sanitizing/formatting applied.
      onRowChange(rowIndex, field, value);
      return;
    }
    // All other numeric fields: strip any existing commas, sanitize, then
    // re-apply thousands separators (e.g. "100000" -> "100,000").
    const cleaned = stripCommas(value).replace(/[^0-9-]/g, "");
    onRowChange(rowIndex, field, formatThousands(cleaned));
  };

  // rowIndex is absolute (relative to the Locked Date Range's start), not
  // relative to the currently displayed window, so edits made while a
  // narrower filter window is showing still land on the correct day.
  const tableData = useMemo(
    () =>
      gasdays.map((day, i) => ({
        gasDay: day ? day.format("MM/DD/YYYY") : "—",
        rowIndex: rowIndexOffset + i,
        ...(rows[rowIndexOffset + i] || {}),
      })),
    [startDate, dayCount, rowIndexOffset, rows]
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: "gasDay",
        header: "Gas Day",
        size: 130,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        Cell: ({ cell }) => (
          <Typography
            sx={{
              fontWeight: 600,
              color: "#1a3a5c",
              fontSize: "14px",
              padding: "8px",
            }}
          >
            {cell.getValue()}
          </Typography>
        ),
      },
      ...columnHeaders.map((col) => ({
        accessorKey: col.key,
        header: col.label,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        Cell: ({ cell, row }) => (
          <TextField
            size="small"
            variant="outlined"
            value={cell.getValue() ?? ""}
            onChange={(e) =>
              isAdmin
                ? handleChange(row.original.rowIndex, col.key, e.target.value, col.allowDecimal)
                : undefined
            }
            // Read-only and visually dimmed when not admin
            InputProps={{ readOnly: !isAdmin }}
            disabled={!isAdmin}
            inputProps={{
              style: {
                textAlign: "center",
                padding: "5px 8px",
              },
              inputMode: col.allowDecimal ? "text" : "numeric",
            }}
            sx={{
              padding: "8px",
              width: "100%",
              minWidth: 90,
              "& .MuiOutlinedInput-root": {
                borderRadius: "6px",
              },
              ...disabledFieldSx,
            }}
          />
        ),
      })),
    ],
    [rows, startDate, dayCount, rowIndexOffset, isAdmin]
  );

  const table = useMaterialReactTable({
    columns,
    data: tableData,
    enableSorting: false,
    enableColumnFilters: false,
    enableFullScreenToggle: false,
    enableColumnActions: false,
    enableColumnOrdering: false,
    enablePagination: false,
    enableGlobalFilter: false,
    enableDensityToggle: false,
    enableTopToolbar: false,
    enableRowActions: false,
    enableRowSelection: false,
    enableHiding: false,
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: "#1a3a5c",
        color: "#fff",
        fontWeight: 700,
        textAlign: "center",
        borderRight: "1px solid rgba(255,255,255,0.12)",
        py: 1.5,
        "& .Mui-TableHeadCell-Content": {
          justifyContent: "center",
        },
      },
    },
    muiTableBodyCellProps: {
      sx: {
        borderRight: "1px solid #e0e7ef",
        py: 0.75,
        px: 1,
        "&:last-child": { borderRight: "none" },
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        border: "1px solid #e0e7ef",
        borderRadius: "10px",
        overflow: "hidden",
        mt: 2,
      },
    },
    muiTableContainerProps: {
      sx: { overflowX: "auto" },
    },
    renderBottomToolbar: () => (
      <Box
        sx={{
          px: 2,
          py: 1,
          borderTop: "1px solid #e0e7ef",
          backgroundColor: "#f7faff",
        }}
      >
        <Typography sx={{ color: "#7a8fa6" }}>
          Total DRV Sendout includes all firm and interruptible deliveries.
        </Typography>
      </Box>
    ),
  });

  return <MaterialReactTable table={table} />;
};

export default ForecastDrvTable;